import { Section } from '../Detail/Section';
import { Radio } from 'antd';
import { PureAnswerEditor } from '../Detail/AnswerEditor';
import { useTranslate } from '@tolgee/react';
import { SupplementKnowledgeAnswer } from 'services/api/supplementKnowledge';
import { useCallback, useEffect, useState } from 'react';
import { QAPairSupportMessageTypes } from 'hooks/business/im/useQAPair';
import { toNumber } from 'lodash-es';
import { MessagesPanel } from '../Detail/MessagesPanel';
import { isExistAnswer } from '../utils';
import { isIdEqual } from 'utils/is';
import { useMessageList } from 'hooks/business/message';

export type Action = 'SELECT' | 'INPUT' | 'NONE';

interface Props {
  conversation?: Conversation.Conversation;
  replyMessage?: Conversation.Message;
  action: Action;
  setAction: (action: Action) => void;
  region: Conversation.Conversation['region'];
  channel: Conversation.Conversation['channel'];
  storeId: string;
  answers: SupplementKnowledgeAnswer[];
  onAnswersChange: (answers: SupplementKnowledgeAnswer[]) => void;
  selectedMessages: Conversation.Message[];
  onSelectedMessagesChange: (messages: Conversation.Message[]) => void;
}

export const SubmitAnswer = (props: Props) => {
  const {
    conversation,
    replyMessage,
    action,
    setAction,
    region,
    channel,
    storeId,
    answers,
    onAnswersChange,
    selectedMessages,
    onSelectedMessagesChange,
  } = props;
  const { t } = useTranslate();
  const messageList = useMessageList();
  const [validateMessageList, setValidateMessageList] = useState<Conversation.Message[]>([]);

  const getValidateMessageList = useCallback(() => {
    if (!replyMessage) {
      return [];
    }
    const { conversationId, sendTime, msgId } = replyMessage;
    return messageList.filter((message) => {
      const { messageType, msgFromType } = message;
      const sameConversation = isIdEqual(message.conversationId, conversationId);
      const sameMessageId = isIdEqual(msgId, message.msgId);
      return (
        !sameMessageId &&
        sameConversation &&
        msgFromType === 'ASSISTANT' &&
        QAPairSupportMessageTypes.includes(messageType) &&
        toNumber(message.sendTime) > toNumber(sendTime)
      );
    });
  }, [replyMessage, messageList]);

  useEffect(() => {
    const validateMessageList = getValidateMessageList();
    setValidateMessageList(validateMessageList);
    if (validateMessageList.length > 0) {
      setAction('SELECT');
    } else if (validateMessageList.length === 0) {
      setAction('INPUT');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section title={t('添加知识')} className="pb-4">
      <div className="text-[#FAAD14] mb-2">
        {isExistAnswer(answers) ? t('ai_err_add_tips1') : t('ai_err_add_tips2')}
      </div>
      <Radio.Group
        className="!mb-4"
        value={action}
        onChange={(e) => {
          setAction(e.target.value as Action);
          if (e.target.value === 'SELECT') {
            setValidateMessageList(getValidateMessageList());
          }
        }}
      >
        {validateMessageList.length > 0 && (
          <Radio value="SELECT">{t('从客服的回复中选择答案')}</Radio>
        )}
        <Radio value="INPUT">{t('手动输入答案')}</Radio>
        <Radio value="NONE">{t('不添加知识入库')}</Radio>
      </Radio.Group>
      {action === 'SELECT' && conversation && (
        <MessagesPanel
          messages={validateMessageList}
          conversation={conversation}
          selectedMessages={selectedMessages}
          onSelectedMessagesChange={onSelectedMessagesChange}
        />
      )}
      {action === 'INPUT' && (
        <PureAnswerEditor
          autoFocus={true}
          region={region}
          channel={channel}
          storeId={storeId}
          answers={answers}
          onAnswersChange={onAnswersChange}
        />
      )}
    </Section>
  );
};
